import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import Button from '../../shared/components/ui/Button';
import FormInput from '../../shared/components/ui/FormInput';
import {
  addLateFeeConfig,
  getlatefeebyId,
  updateLateFeeConfig
} from '../services/api/implementation/impl';
import { useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import mixpanel from 'mixpanel-browser';
import Cookies from 'js-cookie';

// Validation schema
const validationSchema = yup.object().shape({
  gracePeriod: yup
    .number()
    .typeError('Grace Period must be a number')
    .required('Grace Period is required')
    .integer('Grace Period must be a whole number')
    .min(0, 'Grace Period cannot be negative'),
  lateFee: yup
    .number()
    .typeError('Late fee % must be a number')
    .required('Late fee % is required')
    .max(100, 'Late Fee % should not be more than 100%')
    .test('decimal', 'Maximum 1 decimal place allowed', (value) => {
      if (!value) return true;
      const decimalPlaces = value.toString().split('.')[1]?.length || 0;
      return decimalPlaces <= 1;
    })
});

export const LateFee = () => {
  const userId = Cookies.get('userId');
  const { id: projectId } = useParams();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [latefeeId, setlatefeeId] = useState('');

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset
  } = useForm({
    resolver: yupResolver(validationSchema)
  });

  // Fetch existing late fee data (if any)
  const { data: getlatefeeData } = useQuery({
    queryKey: ['getlatefeeID', projectId],
    queryFn: () => getlatefeebyId(parseInt(projectId)),
    enabled: !!projectId
  });

  // Mutation: Add Late Fee
  const { mutate: addFeeConfigHandler } = useMutation({
    mutationFn: (payload) => addLateFeeConfig(payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: (res) => {
      setIsSubmitting(false);
      toast.success('Late fee config created successfully');
      const responseData = res?.data || res;
      const createdId = responseData?.id ?? '';
      if (createdId) {
        setlatefeeId(createdId);
        setIsEdit(true);
      }

      // Optionally reset form with newly created data
      reset({
        lateFee: responseData?.percentage ?? '',
        gracePeriod: responseData?.days ?? ''
      });
    },
    onError: (err) => {
      setIsSubmitting(false);
      toast.error(err?.response?.data?.message || err?.message || 'Error creating late fee config');
      console.error('Error creating new late fee config', err);
    }
  });

  // Mutation: Update Late Fee
  const { mutate: updatelatefee } = useMutation({
    mutationFn: (payload) => updateLateFeeConfig(latefeeId, payload),
    onMutate: () => {
      setIsSubmitting(true);
    },
    onSuccess: (res) => {
      setIsSubmitting(false);
      toast.success('Late fee config updated successfully');
      reset({
        lateFee: res.percentage,
        gracePeriod: res.days
      });
    },
    onError: (err) => {
      setIsSubmitting(false);
      toast.error(err?.response?.data?.message || err?.message || 'Error updating late fee config');
      console.error('Error updating late fee config', err);
    }
  });

  // Form submit handler
  const onSubmitForm = (data) => {
    const payload = {
      project_id: parseInt(projectId),
      days: data.gracePeriod,
      percentage: data.lateFee
    };

    if (isEdit) {
      // Update late fee if record exists
      updatelatefee(payload);
    } else {
      // Otherwise, add new late fee record
      addFeeConfigHandler(payload);
    }
  };

  // Track page view
  useEffect(() => {
    mixpanel.track('Late Fee Viewed', {
      user_id: userId,
      timestamp: new Date().toISOString()
    });
  }, [userId]);

  useEffect(() => {
    // If getlatefeeData has an object, switch to edit mode and prefill form
    if (getlatefeeData && Array.isArray(getlatefeeData) && getlatefeeData.length > 0) {
      const [lateFeeRecord] = getlatefeeData;
      setIsEdit(true);
      setlatefeeId(lateFeeRecord.id);

      reset({
        lateFee: lateFeeRecord.percentage || '',
        gracePeriod: lateFeeRecord.days || ''
      });
    } else {
      // No data means add mode
      setIsEdit(false);
      setlatefeeId('');
      reset({
        lateFee: '',
        gracePeriod: ''
      });
    }
  }, [getlatefeeData, reset]);

  const [maxMonthlyFee, setMaxMonthlyFee] = useState('');

  const handleMonthlyLateFeeChange = (e) => {
    let inputVal = e;

    // 1) Allow only digits and one decimal point
    inputVal = inputVal.replace(/[^\d.]/g, '');

    // 2) Ensure only one decimal point
    const decimalCount = (inputVal.match(/\./g) || []).length;
    if (decimalCount > 1) {
      inputVal = inputVal.replace(/\.+$/, '');
    }

    // 3) Limit to 1 decimal place
    if (inputVal.includes('.')) {
      const [whole, decimal] = inputVal.split('.');
      inputVal = `${whole}.${decimal.slice(0, 1)}`;
    }

    // 4) Check if value exceeds 100
    const numericVal = parseFloat(inputVal);
    if (!isNaN(numericVal) && numericVal > 100) {
      inputVal = '100';
      toast.error('Monthly late fee % cannot exceed 100');
    }

    // 5) Set our local state and form value
    setMaxMonthlyFee(inputVal);
    setValue('lateFee', inputVal);
  };

  const handleGracePeriodChange = (e) => {
    let inputVal = e;
    // Grace period is stored in days, so keep it as non-negative whole numbers only.
    inputVal = inputVal.replace(/\D/g, '');
    setValue('gracePeriod', inputVal);
  };

  const modeLabel = isEdit ? 'Edit Late Fee Settings' : 'Add Late Fee Settings';
  const submitLabel = isSubmitting ? 'Saving...' : isEdit ? 'Update Settings' : 'Save Settings';

  return (
    <div className="mt-3 w-full max-w-none overflow-hidden rounded-lg border border-[#2f5f66] bg-[#295f66] shadow-md">
      <div className="border-b border-[#3f747a] px-4 py-3">
        <span className="text-base font-semibold text-[#d8f2ef]">Finance Settings (Late Fees)</span>
        <p className="mt-1 text-xs font-medium uppercase tracking-wide text-[#9ec5c2]">{modeLabel}</p>
      </div>

      <form
        onSubmit={handleSubmit(onSubmitForm)}
        className="px-4 py-3 [&_input]:rounded-[8px] [&_input]:border-[#0f8a8b] [&_input]:bg-[#295f66] [&_input]:pr-14 [&_input]:text-[#d8f2ef] [&_input]:placeholder:text-[#9ec5c2] [&_input:focus]:ring-0 [&_select]:rounded-[8px] [&_select]:border-[#0f8a8b] [&_select]:bg-[#295f66] [&_select]:text-[#d8f2ef] [&_span]:tracking-[0.01em] [&_span]:text-[#f3ffff]">
        <div className="flex w-full flex-wrap items-end gap-3">
          <div className="relative min-w-[190px] flex-1">
            <FormInput
              width={96}
              label="Monthly Late Fee %"
              isRequired
              register_key="lateFee"
              register={register}
              type="text"
              onType={handleMonthlyLateFeeChange}
              errors={errors.lateFee}
              defaultValuePre={maxMonthlyFee}
            />
            <span className="pointer-events-none absolute bottom-[7px] right-[16px] rounded-md bg-[#1f7278] px-2 py-[2px] text-xs font-semibold leading-none text-[#d8f2ef]">
              %
            </span>
          </div>

          <div className="relative min-w-[190px] flex-1">
            <FormInput
              width={96}
              label="Invoice Grace Period"
              isRequired
              register_key="gracePeriod"
              register={register}
              type="number"
              onType={handleGracePeriodChange}
              errors={errors.gracePeriod}
            />
            <span className="pointer-events-none absolute bottom-[7px] right-[16px] rounded-md bg-[#1f7278] px-2 py-[2px] text-xs font-semibold leading-none text-[#d8f2ef]">
              Days
            </span>
          </div>

          <div className="flex items-end pb-[1px]">
            <Button
              type="submit"
              displayType="secondary"
              disabled={isSubmitting}
              customStyle={{
                backgroundColor: '#f5f7f7',
                borderColor: '#f5f7f7',
                fontWeight: 700,
                fontSize: '13px',
                padding: '8px 14px',
                textTransform: 'uppercase',
                letterSpacing: '0.02em'
              }}>
              {submitLabel}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};
